/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global DockableSideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends DockableSideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          if (!this.isDocked) {
            evt.preventDefault();
            this.open(cartIcon);
          }
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (!this.isDocked) {
            if (evt.key !== ' ') return;
            evt.preventDefault();
            this.open(cartIcon);
          }
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      this.addEventListener('click', this.handleEmptyCart.bind(this));

      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refreshCartDrawer.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Clears the cart
     * @param {object} evt - Event object.
     */
    handleEmptyCart(evt) {
      if (evt.target.matches('.js-empty-cart')) {
        evt.preventDefault();

        // eslint-disable-next-line no-alert, no-restricted-globals
        if (confirm(theme.strings.confirmEmptyCart)) {
          this.querySelectorAll('.cart-item').forEach((cartItem, index) => {
            setTimeout(() => cartItem.classList.add('cart-item--out'), index * 50);
          });

          try {
            fetch(`${theme.routes.cartClear}.js`, {
              method: 'POST'
            }).then((response) => {
              if (!response.ok) throw new Error(response.status);

              // Broadcast individual events to update product cards
              this.querySelectorAll('.cart-item').forEach((cartItem) => {
                this.dispatchEvent(new CustomEvent('on:line-item:change', {
                  bubbles: true,
                  detail: {
                    variantId: cartItem.dataset.variantId,
                    newQuantity: 0
                  }
                }));
              });

              // FIX Bug 1 (18/mai/2026, 2ª iteração): o caminho normal via
              // refreshCartDrawer() → refreshCartItems() não atualizava todas
              // as partes do drawer (subtotal em .cart-drawer__summary,
              // <h2 class="cart-drawer__header">Products (X)</h2> fora do
              // <cart-items>, contador no header). Substituímos por refresh
              // direto e completo: pega o HTML novo da Section Rendering API
              // e reinjeta INNER do <cart-drawer> e do #cart-icon-bubble.
              this.fullRefreshAfterEmpty();
            });
          } catch (error) {
            console.log('Error clearing cart:', error); // eslint-disable-line
            window.location.href = evt.target.href;
          }
        }
      }
    }

    /**
     * Force-refresh of the cart drawer and header bubble after an Empty Cart
     * action. Replaces the inner HTML of <cart-drawer> and #cart-icon-bubble
     * with the fresh server-rendered markup.
     */
    async fullRefreshAfterEmpty() {
      try {
        const cartDrawerSectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');
        const res = await fetch(`/?sections=${cartDrawerSectionId},cart-icon-bubble`);
        if (!res.ok) {
          // Fallback: usa o caminho legado
          this.refreshCartDrawer();
          return;
        }
        const data = await res.json();

        // 1. Substituir o INNER do <cart-drawer id="cart-drawer"> pelo do response
        const drawerHtml = data[cartDrawerSectionId];
        if (drawerHtml) {
          const tmp = document.createElement('template');
          tmp.innerHTML = drawerHtml;
          const newDrawer = tmp.content.querySelector('cart-drawer');
          if (newDrawer) {
            this.innerHTML = newDrawer.innerHTML;
            this.setAttribute('data-empty', 'true');
          }
        }

        // 2. Substituir o INNER do <div id="cart-icon-bubble"> pelo do response.
        //
        // Por que injetar o HTML direto (não tentar extrair sub-elemento):
        // - A section cart-icon-bubble.liquid NÃO renderiza um <div id="cart-icon-bubble">
        //   — esse div só existe inline em sections/header.liquid:633
        // - O response da Section Rendering API tem wrapper <div class="shopify-section"
        //   id="shopify-section-cart-icon-bubble"> mas NÃO contém #cart-icon-bubble
        // - Tentar querySelector('#cart-icon-bubble') retornava null e o badge nunca
        //   atualizava após Empty cart (cliente reportou em 18/mai/2026)
        // - Mesmo padrão de atc-hardened.js:41-47 que funciona para Add to cart
        const bubbleHtml = data['cart-icon-bubble'];
        const liveBubble = document.getElementById('cart-icon-bubble');
        if (bubbleHtml && liveBubble) {
          liveBubble.innerHTML = bubbleHtml;
        }

        // Atualizar contador secundário do drawer (se existir).
        // Lê do liveBubble já atualizado — se cart está vazio, .header__cart-count-number
        // não existe e drawerCount fica "(0)".
        const drawerCount = document.getElementById('cart-drawer__count');
        if (drawerCount) {
          const countEl = liveBubble && liveBubble.querySelector('.header__cart-count-number');
          drawerCount.innerHTML = countEl ? `(${countEl.innerHTML})` : '(0)';
        }

        // 3. Eventos que outros scripts podem escutar (ex: product cards)
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.body.classList.remove('cart-has-items');

        CartDrawer.recalculateCssVarHeights();
      } catch (error) {
        console.log('Error in fullRefreshAfterEmpty:', error); // eslint-disable-line
        // Fallback: usa o caminho legado
        this.refreshCartDrawer();
      }
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refreshCartDrawer(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items[data-empty="false"]');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refreshCartItems();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : null
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;

        // CASO ESPECIAL (fix Bug 1, 18/mai/2026): cart-icon-bubble não é section
        // wrapper — é apenas <div id="cart-icon-bubble"> no header. O selector
        // '.shopify-section' default tenta extrair o INNER da section, mas esse
        // inner JÁ É o <div id="cart-icon-bubble">, causando aninhamento e o
        // contador externo não atualizar. Mesma técnica do atc-hardened.js:41-47
        // e cart-items.js (fix paralelo).
        // ESSE caminho é executado quando "Empty cart" zera o carrinho — o
        // status 400 da Section Rendering API força cart-drawer.refreshCartDrawer(true)
        // que chama renderContents() aqui.
        if (section.id === 'cart-icon-bubble') {
          const fresh = CartDrawer.getElementHTML(
            data.sections[section.section],
            '#cart-icon-bubble'
          );
          if (fresh) el.innerHTML = fresh;

          const drawerCount = document.getElementById('cart-drawer__count');
          if (drawerCount) {
            const html = CartDrawer.getElementHTML(
              data.sections[section.section],
              '#cart-icon-bubble .header__cart-count-number'
            );
            drawerCount.innerHTML = `(${html})`;
          }
          return;
        }

        el.innerHTML = CartDrawer.getElementHTML(
          data.sections[section.section],
          section.selector
        );
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      window.initLazyImages();
      CartDrawer.recalculateCssVarHeights();
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];

      // Append any quick order lists to the sections
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections.push({
            id: `quick-order-list_${sectionId}`,
            section: sectionId,
            selector: `#quick-order-list_${sectionId}`
          });
        }
      });

      return sections;
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      // Fix Bug 1 (18/mai/2026): retornar string vazia se selector não encontrar
      // elemento, em vez de crashar com TypeError ao chamar .innerHTML em null.
      // O crash interrompia o forEach do renderContents() e impedia o cart-drawer
      // (subtotal) de atualizar quando o cart-icon-bubble dava erro de seletor.
      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
